import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ConversationId } from "@dayvise/domain";
import type { ConversationOutputTree } from "@dayvise/client-sdk";
import { saveFileContent } from "./fileDownload.ts";
import { queryKeys } from "./queryKeys.ts";
import { useActiveWorkspaceId, useDayviseClient, useSession } from "./providers.tsx";

export interface ConversationOutputView {
  readonly path: string;
  readonly snapshotFileId: string;
  readonly mimeType: string;
  readonly size: number;
  readonly version: number;
  readonly hash: string;
  readonly runId: string;
  readonly branchId: string;
}

export function conversationOutputViews(tree: ConversationOutputTree | undefined | null): readonly ConversationOutputView[] {
  if (!tree) return [];
  return tree.files.map((file) => ({
    path: file.path,
    snapshotFileId: file.snapshotFileId,
    mimeType: file.mimeType,
    size: file.size,
    version: file.version,
    hash: file.hash,
    runId: file.runId,
    branchId: file.branchId,
  }));
}

export function useConversationOutputs(conversationId: ConversationId | null, branchId: string | null) {
  const client = useDayviseClient();
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    queryKey: queryKeys.conversationOutputs({ workspaceId }, conversationId ?? ("none" as ConversationId), branchId),
    queryFn: () => client.conversations.listOutputs(String(conversationId), branchId === null ? undefined : { branchId }),
    enabled: conversationId !== null && workspaceId !== null,
  });
}

export function useConversationOutputDownload(conversationId: ConversationId | null, branchId: string | null) {
  const client = useDayviseClient();
  const workspaceId = useActiveWorkspaceId();
  const { session } = useSession();
  const identity = JSON.stringify([workspaceId, session.account?.userId ?? null, conversationId === null ? null : String(conversationId), branchId]);
  const current = useRef<string | null>(identity);
  const generation = useRef(0);
  const busy = useRef<string | null>(null);
  if (current.current !== identity) {
    generation.current++;
    busy.current = null;
  }
  current.current = identity;
  useEffect(() => {
    current.current = identity;
    return () => {
      current.current = null;
      generation.current++;
      busy.current = null;
    };
  }, [identity]);
  const [state, setState] = useState<{ identity: string; fileId: string | null; error: string | null }>({ identity, fileId: null, error: null });

  const download = async (output: { readonly snapshotFileId: string; readonly path: string }) => {
    if (conversationId === null) {
      setState({ identity, fileId: null, error: "Open a conversation before downloading an output." });
      return;
    }
    if (busy.current === identity) return;
    busy.current = identity;
    const epoch = generation.current;
    const active = () => current.current === identity && generation.current === epoch;
    setState({ identity, fileId: output.snapshotFileId, error: null });
    try {
      const file = await client.conversations.downloadOutput(String(conversationId), output.snapshotFileId, branchId === null ? undefined : { branchId });
      if (active()) saveFileContent({ bytes: file.bytes, mimeType: file.mimeType, filename: file.filename });
    } catch (error) {
      if (active()) setState({ identity, fileId: null, error: error instanceof Error ? error.message : "Output download failed." });
      return;
    } finally {
      if (active()) busy.current = null;
    }
    if (active()) setState({ identity, fileId: null, error: null });
  };

  return { download, downloadingFileId: state.identity === identity ? state.fileId : null, error: state.identity === identity ? state.error : null };
}
